import { useEffect, useRef } from "react";
import { socket } from "../socket";

export const LIST_REFRESH_DEBOUNCE_MS = 400;

export const BOOKING_LIST_EVENTS = [
  "booking_created",
  "booking_assigned",
  "booking_cancelled",
  "booking_canceled",
  "trip_reassigned",
] as const;

export const ENQUIRY_LIST_EVENTS = [
  "enquiry_created",
  "enquiry_updated",
  "enquiry_to_booking",
] as const;

/**
 * Keeps a list page current without a manual refresh: refetches on the given
 * socket events, when the browser tab becomes visible again, and after the
 * socket reconnects. Pages open as separate browser tabs (see openMenuTab in
 * BookingLayout), so a backgrounded tab that misses an event needs the
 * visibility and reconnect fallbacks to catch up.
 */
export function useListAutoRefresh(
  events: readonly string[],
  refetch: () => void,
  debounceMs: number = LIST_REFRESH_DEBOUNCE_MS
) {
  // Hold the newest closure (branch id + date range) so the subscription is
  // set up once but always calls the current fetch function.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Stable dep so callers can pass an inline array literal.
  const eventsKey = events.join("|");

  useEffect(() => {
    let timer: number | undefined;

    const refreshSoon = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => refetchRef.current(), debounceMs);
    };

    const names = eventsKey ? eventsKey.split("|") : [];
    names.forEach((name) => socket.on(name, refreshSoon));

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshSoon();
    };
    document.addEventListener("visibilitychange", onVisible);

    socket.on("connect", refreshSoon);

    return () => {
      window.clearTimeout(timer);
      names.forEach((name) => socket.off(name, refreshSoon));
      document.removeEventListener("visibilitychange", onVisible);
      socket.off("connect", refreshSoon);
    };
  }, [eventsKey, debounceMs]);
}
